import {
  ArrowsOutSimple,
  CaretDown,
  CheckCircle,
  FastForward,
  Headphones,
  Pause,
  Play,
  Rewind,
  SpeakerHigh,
  SpeakerSlash,
  X,
} from "@phosphor-icons/react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import "./media-player.css";
import { MediaPlayerSidebarDockProvider, SidebarMediaPlayer } from "./media-player-sidebar-dock";
import {
  clampVolume,
  clearAudioProgress,
  DEFAULT_VOLUME,
  type MediaVolumePreference,
  readAudioProgress,
  readMediaVolumePreference,
  saveAudioProgress,
  saveMediaVolumePreference,
} from "./media-player-storage";
import type { MediaPlaybackRequest, MediaPlayerController } from "./media-player-types";
import { streamEmbedUrl } from "./stream-embed-url";

const SKIP_SECONDS = 15;
const STREAM_SDK_URL = "https://embed.cloudflarestream.com/embed/sdk.latest.js";

interface StreamPlayer {
  play(): Promise<void>;
  pause(): void;
  paused: boolean;
  currentTime: number;
  duration: number;
  muted: boolean;
  volume: number;
  addEventListener(event: string, callback: (event?: Event) => void): void;
  removeEventListener(event: string, callback: (event?: Event) => void): void;
}

// The Cloudflare Stream SDK installs itself on window once its script loads.
declare global {
  interface Window {
    Stream?: (element: HTMLIFrameElement) => StreamPlayer;
  }
}

type ActiveMediaSession = MediaPlaybackRequest & {
  id: string;
  started: boolean;
  completed: boolean;
};

const MediaPlayerContext = createContext<MediaPlayerController | null>(null);

let streamSdkPromise: Promise<void> | null = null;

function loadStreamSdk(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("Browser unavailable"));
  if (streamPlayerFactory()) return Promise.resolve();
  if (streamSdkPromise) return streamSdkPromise;

  streamSdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-healthos-stream-sdk="true"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Video controls unavailable")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = STREAM_SDK_URL;
    script.async = true;
    script.dataset.healthosStreamSdk = "true";
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("Video controls unavailable")), {
      once: true,
    });
    document.head.append(script);
  });

  return streamSdkPromise;
}

function streamPlayerFactory(): ((element: HTMLIFrameElement) => StreamPlayer) | undefined {
  return window.Stream;
}

function formatPlaybackTime(value: number): string {
  if (!Number.isFinite(value) || value < 0) return "0:00";
  const total = Math.floor(value);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function MediaPlayerProvider({ children }: { children: ReactNode }) {
  const sessionSequence = useId();
  const sequenceRef = useRef(0);
  const activeRef = useRef<ActiveMediaSession | null>(null);
  const streamPlayerRef = useRef<StreamPlayer | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [session, setSession] = useState<ActiveMediaSession | null>(null);
  const [mode, setMode] = useState<"full" | "mini">("full");
  const [miniExpanded, setMiniExpanded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const volumePreferenceRef = useRef<MediaVolumePreference>(readMediaVolumePreference());
  const [volume, setVolume] = useState(volumePreferenceRef.current.volume);
  const [muted, setMuted] = useState(volumePreferenceRef.current.muted);
  const [streamSdkReady, setStreamSdkReady] = useState(() => Boolean(streamPlayerFactory()));
  const [videoError, setVideoError] = useState(false);
  const [sidebarDockTarget, setSidebarDockTarget] = useState<HTMLDivElement | null>(null);

  const notifyPlayback = useCallback((nextPlaying: boolean) => {
    setPlaying(nextPlaying);
    activeRef.current?.onPlayingChange?.(nextPlaying);
  }, []);

  const pauseUnderlyingMedia = useCallback(() => {
    streamPlayerRef.current?.pause();
    audioRef.current?.pause();
  }, []);

  const persistActiveAudioProgress = useCallback((currentTime?: number) => {
    const current = activeRef.current;
    if (current?.mediaType !== "audio") return;
    const position = currentTime ?? audioRef.current?.currentTime;
    if (position !== undefined) saveAudioProgress(current.audioUrl, position);
  }, []);

  const close = useCallback(
    (reason: "dismissed" | "completed" = "dismissed") => {
      const current = activeRef.current;
      if (!current) return;
      persistActiveAudioProgress();
      pauseUnderlyingMedia();
      notifyPlayback(false);
      activeRef.current = null;
      streamPlayerRef.current = null;
      setMiniExpanded(false);
      setSession(null);
      if (reason === "dismissed") current.onDismiss?.();
    },
    [notifyPlayback, pauseUnderlyingMedia, persistActiveAudioProgress],
  );

  const reportStarted = useCallback(() => {
    const current = activeRef.current;
    if (current?.mediaType !== "video" || current.started) return;
    current.started = true;
    current.onStarted?.({
      secondsWatched: 0,
      durationSeconds: durationOrFallback(
        streamPlayerRef.current?.duration,
        current.durationSeconds,
      ),
    });
  }, []);

  const reportVideoCompleted = useCallback((secondsWatched: number, durationSeconds: number) => {
    const current = activeRef.current;
    if (current?.mediaType !== "video" || current.completed) return;
    current.completed = true;
    current.onComplete?.({ secondsWatched, durationSeconds });
  }, []);

  const reportCompletionThreshold = useCallback(
    (secondsWatched: number, durationSeconds: number) => {
      if (durationSeconds <= 0 || secondsWatched < durationSeconds / 2) return;
      reportVideoCompleted(secondsWatched, durationSeconds);
    },
    [reportVideoCompleted],
  );

  const openSession = useCallback(
    (request: MediaPlaybackRequest) => {
      const previous = activeRef.current;
      if (previous) {
        persistActiveAudioProgress();
        pauseUnderlyingMedia();
        previous.onPlayingChange?.(false);
      }

      const next: ActiveMediaSession = {
        ...request,
        id: `${sessionSequence}-${sequenceRef.current++}`,
        started: false,
        completed: false,
      };
      activeRef.current = next;
      setSession(next);
      setMode("full");
      setMiniExpanded(false);
      setPlaying(false);
      setCurrentTime(0);
      setDuration(request.mediaType === "video" ? (request.durationSeconds ?? 0) : 0);
      const preference = readMediaVolumePreference();
      volumePreferenceRef.current = preference;
      setMuted(preference.muted);
      setVolume(preference.volume);
      setStreamSdkReady(Boolean(streamPlayerFactory()));
      setVideoError(false);
    },
    [pauseUnderlyingMedia, persistActiveAudioProgress, sessionSequence],
  );

  const controller = useMemo<MediaPlayerController>(
    () => ({
      playVideo: (request) => openSession({ ...request, mediaType: "video" }),
      playAudio: (request) => openSession({ ...request, mediaType: "audio" }),
    }),
    [openSession],
  );

  useEffect(() => {
    return () => {
      persistActiveAudioProgress();
      pauseUnderlyingMedia();
      activeRef.current?.onPlayingChange?.(false);
    };
  }, [pauseUnderlyingMedia, persistActiveAudioProgress]);

  useEffect(() => {
    if (session?.mediaType !== "video" || streamSdkReady) return;
    let cancelled = false;

    void loadStreamSdk()
      .then(() => {
        if (!cancelled && activeRef.current?.id === session.id) setStreamSdkReady(true);
      })
      .catch(() => {
        if (!cancelled) setVideoError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [session, streamSdkReady]);

  useEffect(() => {
    if (session?.mediaType !== "video" || !streamSdkReady || !iframeRef.current) return;
    const createStreamPlayer = streamPlayerFactory();
    if (!createStreamPlayer || activeRef.current?.id !== session.id) return;

    const player = createStreamPlayer(iframeRef.current);
    streamPlayerRef.current = player;
    const preference = volumePreferenceRef.current;
    player.volume = preference.volume;
    player.muted = preference.muted;
    setVolume(preference.volume);
    setMuted(preference.muted);

    const syncTime = () => {
      if (activeRef.current?.id !== session.id) return;
      setCurrentTime(player.currentTime);
      const nextDuration = durationOrFallback(player.duration, session.durationSeconds);
      if (nextDuration > 0) setDuration(nextDuration);
      reportCompletionThreshold(player.currentTime, nextDuration);
    };
    const onPlay = () => {
      if (activeRef.current?.id !== session.id) return;
      notifyPlayback(true);
      reportStarted();
    };
    const onPause = () => {
      if (activeRef.current?.id !== session.id) return;
      notifyPlayback(false);
    };
    const onEnded = () => {
      if (activeRef.current?.id !== session.id) return;
      notifyPlayback(false);
      const completedDuration = durationOrFallback(player.duration, session.durationSeconds);
      setCurrentTime(completedDuration);
      reportVideoCompleted(completedDuration, completedDuration);
      close("completed");
    };
    const onVolumeChange = () => {
      if (activeRef.current?.id !== session.id) return;
      setMuted(player.muted);
      setVolume(clampVolume(player.volume));
    };

    player.addEventListener("loadedmetadata", syncTime);
    player.addEventListener("durationchange", syncTime);
    player.addEventListener("timeupdate", syncTime);
    player.addEventListener("play", onPlay);
    player.addEventListener("pause", onPause);
    player.addEventListener("ended", onEnded);
    player.addEventListener("volumechange", onVolumeChange);
    syncTime();
    void player.play().catch(() => {
      player.muted = true;
      setMuted(true);
      void player.play().catch(() => notifyPlayback(false));
    });

    return () => {
      player.removeEventListener("loadedmetadata", syncTime);
      player.removeEventListener("durationchange", syncTime);
      player.removeEventListener("timeupdate", syncTime);
      player.removeEventListener("play", onPlay);
      player.removeEventListener("pause", onPause);
      player.removeEventListener("ended", onEnded);
      player.removeEventListener("volumechange", onVolumeChange);
      if (streamPlayerRef.current === player) streamPlayerRef.current = null;
    };
  }, [
    close,
    notifyPlayback,
    reportCompletionThreshold,
    reportStarted,
    reportVideoCompleted,
    session,
    streamSdkReady,
  ]);

  const togglePlayback = useCallback(async () => {
    const current = activeRef.current;
    if (!current) return;
    if (current.mediaType === "audio") {
      const audio = audioRef.current;
      if (!audio) return;
      if (audio.paused) {
        try {
          await audio.play();
        } catch {
          notifyPlayback(false);
        }
      } else {
        audio.pause();
      }
      return;
    }

    const player = streamPlayerRef.current;
    if (!player) return;
    if (!playing) {
      try {
        await player.play();
      } catch {
        notifyPlayback(false);
      }
    } else {
      player.pause();
    }
  }, [notifyPlayback, playing]);

  const seekTo = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(duration, next));
      const current = activeRef.current;
      if (!current) return;
      if (current.mediaType === "audio" && audioRef.current) {
        audioRef.current.currentTime = clamped;
        saveAudioProgress(current.audioUrl, clamped);
      }
      if (current.mediaType === "video" && streamPlayerRef.current) {
        streamPlayerRef.current.currentTime = clamped;
      }
      setCurrentTime(clamped);
    },
    [duration],
  );

  const setMediaMuted = useCallback(
    (nextMuted: boolean) => {
      const current = activeRef.current;
      if (!current) return;
      const restoredVolume = !nextMuted && volume === 0 ? DEFAULT_VOLUME : volume;
      if (current.mediaType === "audio" && audioRef.current) {
        audioRef.current.volume = restoredVolume;
        audioRef.current.muted = nextMuted;
      }
      if (current.mediaType === "video" && streamPlayerRef.current) {
        streamPlayerRef.current.volume = restoredVolume;
        streamPlayerRef.current.muted = nextMuted;
        if (!nextMuted && streamPlayerRef.current.paused) {
          void streamPlayerRef.current.play().catch(() => notifyPlayback(false));
        }
      }
      const preference = { muted: nextMuted, volume: restoredVolume };
      volumePreferenceRef.current = preference;
      saveMediaVolumePreference(preference);
      setVolume(restoredVolume);
      setMuted(nextMuted);
    },
    [notifyPlayback, volume],
  );

  const setMediaVolume = useCallback(
    (nextVolume: number) => {
      const normalized = clampVolume(nextVolume);
      const current = activeRef.current;
      if (!current) return;
      if (current.mediaType === "audio" && audioRef.current) {
        audioRef.current.volume = normalized;
        audioRef.current.muted = normalized === 0;
      }
      if (current.mediaType === "video" && streamPlayerRef.current) {
        streamPlayerRef.current.volume = normalized;
        streamPlayerRef.current.muted = normalized === 0;
        if (normalized > 0 && streamPlayerRef.current.paused) {
          void streamPlayerRef.current.play().catch(() => notifyPlayback(false));
        }
      }
      const preference = { muted: normalized === 0, volume: normalized };
      volumePreferenceRef.current = preference;
      saveMediaVolumePreference(preference);
      setVolume(normalized);
      setMuted(normalized === 0);
    },
    [notifyPlayback],
  );

  const completeVideo = useCallback(() => {
    const current = activeRef.current;
    if (current?.mediaType !== "video") return;
    const finalDuration = durationOrFallback(duration, current.durationSeconds);
    reportVideoCompleted(currentTime, finalDuration);
    close("completed");
  }, [close, currentTime, duration, reportVideoCompleted]);

  return (
    <MediaPlayerSidebarDockProvider register={setSidebarDockTarget}>
      <MediaPlayerContext.Provider value={controller}>
        {children}
        {session ? (
          <MediaPlayer
            audioRef={audioRef}
            iframeRef={iframeRef}
            session={session}
            mode={mode}
            miniExpanded={miniExpanded}
            playing={playing}
            currentTime={currentTime}
            duration={duration}
            volume={volume}
            muted={muted}
            streamSdkReady={streamSdkReady}
            videoError={videoError}
            sidebarDockTarget={sidebarDockTarget}
            onTogglePlayback={() => void togglePlayback()}
            onSeek={seekTo}
            onSetMuted={setMediaMuted}
            onSetVolume={setMediaVolume}
            onMinimise={() => {
              setMode("mini");
              setMiniExpanded(false);
            }}
            onExpand={() => setMode("full")}
            onExpandMini={() => setMiniExpanded(true)}
            onClose={() => close()}
            onComplete={completeVideo}
            onAudioEvent={(event) => {
              const audio = event.currentTarget;
              const current = activeRef.current;
              if (current?.mediaType === "audio" && event.type === "loadedmetadata") {
                const savedPosition = readAudioProgress(current.audioUrl);
                const duration = audio.duration;
                if (savedPosition > 0 && (!Number.isFinite(duration) || savedPosition < duration)) {
                  audio.currentTime = savedPosition;
                } else if (Number.isFinite(duration) && savedPosition >= duration) {
                  clearAudioProgress(current.audioUrl);
                }
              }
              if (current?.mediaType === "audio" && event.type === "timeupdate") {
                saveAudioProgress(current.audioUrl, audio.currentTime);
              }
              setCurrentTime(audio.currentTime);
              if (Number.isFinite(audio.duration) && audio.duration > 0)
                setDuration(audio.duration);
            }}
            onAudioPlay={() => notifyPlayback(true)}
            onAudioPause={() => notifyPlayback(false)}
            onAudioEnded={() => {
              const current = activeRef.current;
              if (current?.mediaType === "audio") clearAudioProgress(current.audioUrl);
              notifyPlayback(false);
              setCurrentTime(duration);
            }}
            onAudioVolumeChange={(event) => {
              setMuted(event.currentTarget.muted);
              setVolume(clampVolume(event.currentTarget.volume));
            }}
          />
        ) : null}
      </MediaPlayerContext.Provider>
    </MediaPlayerSidebarDockProvider>
  );
}

export function useMediaPlayer(): MediaPlayerController {
  const controller = useContext(MediaPlayerContext);
  if (!controller) {
    throw new Error("useMediaPlayer must be used inside MediaPlayerProvider");
  }
  return controller;
}

function MediaPlayer({
  audioRef,
  iframeRef,
  session,
  mode,
  miniExpanded,
  playing,
  currentTime,
  duration,
  volume,
  muted,
  streamSdkReady,
  videoError,
  sidebarDockTarget,
  onTogglePlayback,
  onSeek,
  onSetMuted,
  onSetVolume,
  onMinimise,
  onExpand,
  onExpandMini,
  onClose,
  onComplete,
  onAudioEvent,
  onAudioPlay,
  onAudioPause,
  onAudioEnded,
  onAudioVolumeChange,
}: {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  session: ActiveMediaSession;
  mode: "full" | "mini";
  miniExpanded: boolean;
  playing: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  muted: boolean;
  streamSdkReady: boolean;
  videoError: boolean;
  sidebarDockTarget: HTMLDivElement | null;
  onTogglePlayback: () => void;
  onSeek: (next: number) => void;
  onSetMuted: (muted: boolean) => void;
  onSetVolume: (volume: number) => void;
  onMinimise: () => void;
  onExpand: () => void;
  onExpandMini: () => void;
  onClose: () => void;
  onComplete: () => void;
  onAudioEvent: (event: React.SyntheticEvent<HTMLAudioElement>) => void;
  onAudioPlay: () => void;
  onAudioPause: () => void;
  onAudioEnded: () => void;
  onAudioVolumeChange: (event: React.SyntheticEvent<HTMLAudioElement>) => void;
}) {
  const portalTarget = typeof document === "undefined" ? null : document.body;
  if (!portalTarget) return null;

  const audioElement =
    session.mediaType === "audio" ? (
      // biome-ignore lint/a11y/useMediaCaption: CMS podcast audio has no published caption track.
      <audio
        ref={audioRef}
        src={session.audioUrl}
        preload="metadata"
        onLoadedMetadata={onAudioEvent}
        onDurationChange={onAudioEvent}
        onTimeUpdate={onAudioEvent}
        onPlay={onAudioPlay}
        onPause={onAudioPause}
        onEnded={onAudioEnded}
        onVolumeChange={onAudioVolumeChange}
      />
    ) : null;

  const miniPlayer = (
    <MiniPlayer
      session={session}
      playing={playing}
      currentTime={currentTime}
      duration={duration}
      expandedFromSidebar={miniExpanded}
      onTogglePlayback={onTogglePlayback}
      onSeek={onSeek}
      onExpand={onExpand}
      onClose={onClose}
    />
  );
  const dockedInSidebar =
    session.mediaType === "audio" && mode === "mini" && !miniExpanded && Boolean(sidebarDockTarget);
  const controls =
    mode === "full" ? (
      <FullPlayer
        session={session}
        playing={playing}
        currentTime={currentTime}
        duration={duration}
        volume={volume}
        muted={muted}
        videoError={videoError}
        onTogglePlayback={onTogglePlayback}
        onSeek={onSeek}
        onSetMuted={onSetMuted}
        onSetVolume={onSetVolume}
        onMinimise={onMinimise}
        onClose={onClose}
        onComplete={onComplete}
      />
    ) : dockedInSidebar && !miniExpanded ? (
      <div className="md:hidden">{miniPlayer}</div>
    ) : (
      miniPlayer
    );

  const player =
    session.mediaType === "video" ? (
      <div
        className={
          mode === "full"
            ? "fixed inset-0 z-[1000] overflow-hidden bg-navy"
            : dockedInSidebar
              ? "media-mini-player media-mini-player--docked fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-[max(1rem,env(safe-area-inset-left))] z-[1000] aspect-video w-[min(26rem,calc(100vw-2rem))] overflow-hidden rounded-2xl bg-surface-tertiary shadow-modal"
              : "media-mini-player fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-[max(1rem,env(safe-area-inset-left))] z-[1000] aspect-video w-[min(26rem,calc(100vw-2rem))] overflow-hidden rounded-2xl bg-surface-tertiary shadow-modal"
        }
      >
        {streamSdkReady ? (
          <iframe
            ref={iframeRef}
            src={streamEmbedUrl(session.videoUrl)}
            className="absolute inset-0 h-full w-full"
            title={session.title}
            allow="autoplay; encrypted-media; picture-in-picture"
            style={{ border: "none" }}
          />
        ) : null}
        {controls}
      </div>
    ) : (
      <>
        {audioElement}
        {controls}
      </>
    );
  const sidebarPlayer =
    dockedInSidebar && sidebarDockTarget
      ? createPortal(
          <SidebarMediaPlayer
            session={session}
            playing={playing}
            currentTime={currentTime}
            duration={duration}
            onExpand={onExpandMini}
            onTogglePlayback={onTogglePlayback}
            onClose={onClose}
          />,
          sidebarDockTarget,
        )
      : null;

  return (
    <>
      {createPortal(player, portalTarget)}
      {sidebarPlayer}
    </>
  );
}

function FullPlayer({
  session,
  playing,
  currentTime,
  duration,
  volume,
  muted,
  videoError,
  onTogglePlayback,
  onSeek,
  onSetMuted,
  onSetVolume,
  onMinimise,
  onClose,
  onComplete,
}: {
  session: ActiveMediaSession;
  playing: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  muted: boolean;
  videoError: boolean;
  onTogglePlayback: () => void;
  onSeek: (next: number) => void;
  onSetMuted: (muted: boolean) => void;
  onSetVolume: (volume: number) => void;
  onMinimise: () => void;
  onClose: () => void;
  onComplete: () => void;
}) {
  if (session.mediaType === "audio") {
    return (
      <div className="fixed inset-0 z-[1000] flex items-end bg-navy/55 p-4 sm:items-center sm:justify-center sm:p-8">
        <section
          role="dialog"
          aria-modal="true"
          aria-label={`Podcast player: ${session.title}`}
          className="relative grid max-h-full w-full max-w-4xl overflow-auto rounded-2xl bg-surface-elevated shadow-modal lg:grid-cols-[minmax(15rem,0.72fr)_minmax(0,1.28fr)]"
        >
          {session.imageUrl ? (
            <img
              src={session.imageUrl}
              alt=""
              className="aspect-[4/3] h-full w-full object-cover lg:aspect-auto"
            />
          ) : (
            <div className="grid min-h-64 place-items-center bg-surface-secondary text-ink-brand">
              <Headphones size={52} weight="duotone" aria-hidden="true" />
            </div>
          )}
          <div className="flex min-w-0 flex-col p-6 sm:p-8 lg:p-10">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                {session.podcastTitle ? (
                  <p className="m-0 font-mono text-2xs uppercase tracking-[0.16em] text-ink-tertiary">
                    {session.podcastTitle}
                  </p>
                ) : null}
                <h2 className="m-0 mt-2 max-w-[18ch] font-title text-3xl font-medium leading-[1.04] text-ink-heading sm:text-[2.5rem]">
                  {session.title}
                </h2>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <MinimiseButton onClick={onMinimise} />
                <PlayerChromeButton label="Close player" onClick={onClose}>
                  <X size={18} weight="bold" aria-hidden="true" />
                </PlayerChromeButton>
              </div>
            </div>
            {session.summary ? (
              <p className="mb-0 mt-5 max-w-prose text-base leading-relaxed text-ink-secondary">
                {session.summary}
              </p>
            ) : null}
            <div className="mt-auto pt-10">
              <PlaybackProgress currentTime={currentTime} duration={duration} onSeek={onSeek} />
              <div className="mt-6 flex flex-wrap items-center justify-between gap-x-8 gap-y-5 border-t border-border-subtle pt-5">
                <div className="flex items-center gap-2">
                  <VolumeControls
                    muted={muted}
                    volume={volume}
                    onSetMuted={onSetMuted}
                    onSetVolume={onSetVolume}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <TransportButton
                    label={`Skip back ${SKIP_SECONDS} seconds`}
                    onClick={() => onSeek(currentTime - SKIP_SECONDS)}
                  >
                    <Rewind size={20} weight="fill" aria-hidden="true" />
                  </TransportButton>
                  <PlayPauseButton playing={playing} onClick={onTogglePlayback} />
                  <TransportButton
                    label={`Skip forward ${SKIP_SECONDS} seconds`}
                    onClick={() => onSeek(currentTime + SKIP_SECONDS)}
                  >
                    <FastForward size={20} weight="fill" aria-hidden="true" />
                  </TransportButton>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const progress = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;
  const remaining = Math.max(0, duration - currentTime);

  return (
    <div
      className="absolute inset-0"
      role="dialog"
      aria-modal="true"
      aria-label={`${session.kind} player: ${session.title}`}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 bg-linear-to-b from-navy/90 via-navy/45 to-transparent p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="max-w-2xl text-ink-on-brand">
            <p className="m-0 font-mono text-2xs uppercase tracking-[0.16em] text-white/65">
              {session.kind} video
            </p>
            <h2 className="m-0 mt-2 font-title text-2xl font-medium leading-tight sm:text-3xl">
              {session.title}
            </h2>
            {session.description ? (
              <p className="mb-0 mt-2 line-clamp-2 text-sm leading-relaxed text-white/80">
                {session.description}
              </p>
            ) : null}
          </div>
          <div className="pointer-events-auto flex items-center gap-2">
            <MinimiseButton dark onClick={onMinimise} />
            <PlayerChromeButton dark label="Close player" onClick={onClose}>
              <X size={18} weight="bold" aria-hidden="true" />
            </PlayerChromeButton>
          </div>
        </div>
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-linear-to-t from-navy/95 via-navy/60 to-transparent px-4 pb-4 pt-16 sm:px-6 sm:pb-6">
        <div className="pointer-events-auto mx-auto max-w-5xl xl:relative">
          <div className="text-ink-on-brand">
            <div className="mb-5 flex items-center gap-2 xl:absolute xl:left-0 xl:top-1/2 xl:-ml-6 xl:mb-0 xl:-translate-x-full xl:-translate-y-1/2">
              <VolumeControls
                dark
                muted={muted}
                volume={volume}
                onSetMuted={onSetMuted}
                onSetVolume={onSetVolume}
              />
              {videoError ? (
                <span className="text-xs text-white/75">Video controls unavailable.</span>
              ) : null}
            </div>
            <div className="mb-2 flex items-center justify-between text-xs text-gray-300 sm:text-sm">
              <span className="flex items-center gap-2">
                <span
                  className={`h-2 w-2 rounded-full ${
                    playing ? "animate-pulse bg-teal-400" : "bg-gray-400"
                  }`}
                />
                {playing
                  ? `${session.kind} in Progress`
                  : progress === 100
                    ? `${session.kind} Complete`
                    : "Paused"}
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={onComplete}
                  aria-label={`Mark ${session.kind.toLowerCase()} as done`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-medium text-white/90 transition-colors hover:border-white/20 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-300"
                >
                  <CheckCircle size={14} weight="fill" aria-hidden="true" />
                  Mark as done
                </button>
                <span>
                  {duration > 0
                    ? `${formatPlaybackTime(currentTime)} / ${formatPlaybackTime(duration)}`
                    : "Loading..."}
                </span>
              </div>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-600/50 sm:h-2">
              <div
                className="h-full rounded-full bg-linear-to-r from-teal-400 to-teal-600 transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            {duration > 0 ? (
              <div className="mt-1 flex items-center justify-between text-xs text-gray-400">
                <span>{Math.round(progress)}% complete</span>
                <span>{formatPlaybackTime(remaining)} remaining</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniPlayer({
  session,
  playing,
  currentTime,
  duration,
  expandedFromSidebar = false,
  onTogglePlayback,
  onSeek,
  onExpand,
  onClose,
}: {
  session: ActiveMediaSession;
  playing: boolean;
  currentTime: number;
  duration: number;
  expandedFromSidebar?: boolean;
  onTogglePlayback: () => void;
  onSeek: (next: number) => void;
  onExpand: () => void;
  onClose: () => void;
}) {
  const isVideo = session.mediaType === "video";
  const progress = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;
  const darkRailStyle = {
    background: `linear-gradient(to right, var(--color-teal-300) 0%, var(--color-teal-300) ${progress}%, color-mix(in srgb, white 32%, transparent) ${progress}%, color-mix(in srgb, white 32%, transparent) 100%)`,
  };
  const lightRailStyle = {
    background: `linear-gradient(to right, var(--color-teal-700) 0%, var(--color-teal-700) ${progress}%, var(--color-mist) ${progress}%, var(--color-mist) 100%)`,
  };

  if (isVideo) {
    return (
      <section
        aria-label={`Mini player: ${session.title}`}
        className="absolute inset-0 z-10 pointer-events-auto text-ink-on-brand"
      >
        <div className="absolute inset-x-0 bottom-0 bg-linear-to-t from-navy via-navy/90 to-transparent px-5 pb-11 pt-16">
          <div className="flex items-end justify-between gap-4">
            <button
              type="button"
              aria-label="Open full player"
              onClick={onExpand}
              className="min-w-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
            >
              <p className="m-0 font-mono text-2xs uppercase tracking-[0.14em] text-white/65">
                Playing in mini player
              </p>
              <p className="m-0 mt-1 truncate font-sans text-sm font-medium text-white">
                {session.title}
              </p>
            </button>
            <div className="flex shrink-0 items-center gap-3">
              <button
                type="button"
                aria-label="Open full player"
                onClick={onExpand}
                className="inline-flex h-9 items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-3 font-sans text-xs font-medium text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-300"
              >
                <ArrowsOutSimple size={15} weight="bold" aria-hidden="true" />
                Open
              </button>
              <PlayerChromeButton
                dark
                label={playing ? "Pause" : "Play"}
                onClick={onTogglePlayback}
              >
                {playing ? (
                  <Pause size={17} weight="fill" aria-hidden="true" />
                ) : (
                  <Play size={17} weight="fill" aria-hidden="true" />
                )}
              </PlayerChromeButton>
              <PlayerChromeButton dark label="Close player" onClick={onClose}>
                <X size={16} weight="bold" aria-hidden="true" />
              </PlayerChromeButton>
            </div>
          </div>
        </div>
        <input
          type="range"
          min={0}
          max={duration > 0 ? duration : 0}
          step={0.1}
          value={duration > 0 ? currentTime : 0}
          disabled={duration <= 0}
          aria-label="Playback position"
          onChange={(event) => onSeek(Number(event.target.value))}
          style={darkRailStyle}
          className="absolute bottom-4 left-5 h-2 w-[calc(100%-2.5rem)] cursor-pointer appearance-none rounded-full outline-none disabled:cursor-default [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-teal-300 [&::-moz-range-track]:h-2 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:mt-[-4px] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-teal-300"
        />
      </section>
    );
  }

  return (
    <section
      aria-label={`Mini player: ${session.title}`}
      className={`media-mini-player fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-[max(1rem,env(safe-area-inset-left))] z-[1000] w-[min(28rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-border-hairline bg-surface-elevated shadow-modal ${
        expandedFromSidebar ? "media-mini-player--from-sidebar" : ""
      }`}
    >
      <div className="grid min-w-0 grid-cols-[5.5rem_minmax(0,1fr)_auto_auto] items-center gap-4 p-4 pr-5">
        <button
          type="button"
          aria-label="Open full player"
          onClick={onExpand}
          className="h-[5.5rem] overflow-hidden rounded-xl bg-surface-secondary text-ink-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        >
          {session.imageUrl ? (
            <img src={session.imageUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="grid h-full place-items-center">
              <Headphones size={28} weight="duotone" aria-hidden="true" />
            </span>
          )}
        </button>
        <button
          type="button"
          aria-label="Open full player"
          onClick={onExpand}
          className="min-w-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        >
          {session.podcastTitle ? (
            <p className="m-0 truncate font-mono text-2xs uppercase tracking-[0.12em] text-ink-tertiary">
              {session.podcastTitle}
            </p>
          ) : null}
          <p className="m-0 mt-1 truncate font-sans text-sm font-medium text-ink-heading">
            {session.title}
          </p>
          <p className="m-0 mt-1 font-mono text-2xs tabular-nums text-ink-tertiary">
            {formatPlaybackTime(currentTime)} / {formatPlaybackTime(duration)}
          </p>
        </button>
        <PlayPauseButton playing={playing} onClick={onTogglePlayback} compact />
        <PlayerChromeButton label="Close player" onClick={onClose}>
          <X size={16} weight="bold" aria-hidden="true" />
        </PlayerChromeButton>
      </div>
      <div className="border-t border-border-subtle px-5 pb-3 pt-3">
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-2xs uppercase tracking-[0.12em] text-ink-tertiary">
            Playing in mini player
          </span>
          <button
            type="button"
            aria-label="Open full player"
            onClick={onExpand}
            className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-ink-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
          >
            <ArrowsOutSimple size={14} weight="bold" aria-hidden="true" />
            Open player
          </button>
        </div>
        <div className="mt-2 py-1">
          <input
            type="range"
            min={0}
            max={duration > 0 ? duration : 0}
            step={0.1}
            value={duration > 0 ? currentTime : 0}
            disabled={duration <= 0}
            aria-label="Playback position"
            onChange={(event) => onSeek(Number(event.target.value))}
            style={lightRailStyle}
            className="h-2 w-full cursor-pointer appearance-none rounded-full outline-none disabled:cursor-default [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-teal-700 [&::-moz-range-track]:h-2 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:mt-[-4px] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-teal-700"
          />
        </div>
      </div>
    </section>
  );
}

function PlaybackProgress({
  currentTime,
  duration,
  onSeek,
  dark = false,
}: {
  currentTime: number;
  duration: number;
  onSeek: (next: number) => void;
  dark?: boolean;
}) {
  const progress = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;
  const railStyle = {
    background: `linear-gradient(to right, ${dark ? "var(--color-teal-300)" : "var(--color-ink-brand)"} 0%, ${dark ? "var(--color-teal-300)" : "var(--color-ink-brand)"} ${progress}%, ${dark ? "color-mix(in srgb, white 28%, transparent)" : "var(--color-surface-track)"} ${progress}%, ${dark ? "color-mix(in srgb, white 28%, transparent)" : "var(--color-surface-track)"} 100%)`,
  };
  return (
    <div className="flex items-center gap-3">
      <span
        className={`w-10 font-mono text-xs tabular-nums ${dark ? "text-white/75" : "text-ink-tertiary"}`}
      >
        {formatPlaybackTime(currentTime)}
      </span>
      <input
        type="range"
        min={0}
        max={duration > 0 ? duration : 0}
        step={0.1}
        value={duration > 0 ? currentTime : 0}
        disabled={duration <= 0}
        aria-label="Playback position"
        onChange={(event) => onSeek(Number(event.target.value))}
        style={railStyle}
        className="h-2 min-w-0 flex-1 cursor-pointer appearance-none rounded-full outline-none disabled:cursor-default [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-teal-600 [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-thumb]:mt-[-4px] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-teal-600"
      />
      <span
        className={`w-10 text-right font-mono text-xs tabular-nums ${dark ? "text-white/75" : "text-ink-tertiary"}`}
      >
        {formatPlaybackTime(duration)}
      </span>
    </div>
  );
}

function VolumeControls({
  muted,
  volume,
  onSetMuted,
  onSetVolume,
  dark = false,
}: {
  muted: boolean;
  volume: number;
  onSetMuted: (muted: boolean) => void;
  onSetVolume: (volume: number) => void;
  dark?: boolean;
}) {
  const restoredVolume = volume === 0 ? DEFAULT_VOLUME : volume;
  const volumePercent = Math.round((muted ? 0 : volume) * 100);
  return (
    <div className="flex items-center gap-2">
      <PlayerChromeButton
        dark={dark}
        label={muted ? "Unmute" : "Mute"}
        onClick={() => onSetMuted(!muted)}
      >
        {muted ? (
          <SpeakerSlash size={18} weight="fill" aria-hidden="true" />
        ) : (
          <SpeakerHigh size={18} weight="fill" aria-hidden="true" />
        )}
      </PlayerChromeButton>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={muted ? 0 : volume}
        aria-label="Volume"
        aria-valuetext={`${volumePercent} percent`}
        onChange={(event) => onSetVolume(Number(event.target.value))}
        onDoubleClick={() => onSetVolume(restoredVolume)}
        style={{
          background: `linear-gradient(to right, ${dark ? "var(--color-teal-300)" : "var(--color-ink-brand)"} 0%, ${dark ? "var(--color-teal-300)" : "var(--color-ink-brand)"} ${volumePercent}%, ${dark ? "color-mix(in srgb, white 28%, transparent)" : "var(--color-surface-track)"} ${volumePercent}%, ${dark ? "color-mix(in srgb, white 28%, transparent)" : "var(--color-surface-track)"} 100%)`,
        }}
        className="h-2 w-24 cursor-pointer appearance-none rounded-full outline-none [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-teal-600 [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-thumb]:mt-[-4px] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-teal-600"
      />
    </div>
  );
}

function MinimiseButton({ dark = false, onClick }: { dark?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Minimise player"
      onClick={onClick}
      className={`inline-flex h-9 items-center gap-1.5 rounded-full px-3 font-sans text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus ${
        dark
          ? "bg-white/12 text-white hover:bg-white/20"
          : "bg-surface-secondary text-ink-brand hover:bg-teal-100"
      }`}
    >
      <CaretDown size={16} weight="bold" aria-hidden="true" />
      Continue browsing
    </button>
  );
}

function PlayerChromeButton({
  label,
  onClick,
  children,
  dark = false,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  dark?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`grid h-9 w-9 shrink-0 place-items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus ${
        dark
          ? "bg-white/12 text-white hover:bg-white/20"
          : "bg-surface-tertiary text-ink-heading hover:bg-surface-secondary"
      }`}
    >
      {children}
    </button>
  );
}

function TransportButton({
  label,
  onClick,
  children,
  dark = false,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  dark?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`grid h-10 min-w-10 place-items-center rounded-full px-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus ${
        dark ? "text-white hover:bg-white/12" : "text-ink-heading hover:bg-surface-secondary"
      }`}
    >
      {children}
    </button>
  );
}

function PlayPauseButton({
  playing,
  onClick,
  disabled = false,
  dark = false,
  compact = false,
}: {
  playing: boolean;
  onClick: () => void;
  disabled?: boolean;
  dark?: boolean;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={playing ? "Pause" : "Play"}
      onClick={onClick}
      disabled={disabled}
      className={`grid place-items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus disabled:cursor-default disabled:opacity-50 ${compact ? "h-11 w-11" : "h-12 w-12"} ${
        dark
          ? "bg-white text-ink-heading hover:bg-white/85"
          : "bg-ink-brand text-ink-on-brand hover:bg-teal-800"
      }`}
    >
      {playing ? (
        <Pause size={compact ? 19 : 21} weight="fill" aria-hidden="true" />
      ) : (
        <Play
          size={compact ? 19 : 21}
          weight="fill"
          className="translate-x-px"
          aria-hidden="true"
        />
      )}
    </button>
  );
}

function durationOrFallback(value: number | undefined, fallback: number | null): number {
  if (value && Number.isFinite(value) && value > 0) return value;
  return fallback ?? 0;
}
